import React, {
  Reducer,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import {
  HttpError,
  VurderingsinnstillingerDto,
  VurderingsrollerDto,
  VurderingstyperDto,
} from '../../api/generated/studiested-generated-api';
import * as css from './vurderingsinnstillinger.module.scss';
import Button from '../../components/button';
import Checkbox from '../../components/input/checkbox';
import { useActivityContextUHA } from '../activitycontexts';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';
import { useIntl } from 'react-intl';
import translations from './vurderingsinnstillinger.i18n';
import { GenericActionWithPayload } from '../../util';

enum Roller {
  Underviser = 'underviser',
  Veileder = 'veileder',
  Student = 'student',
}

enum Vurderingstyper {
  Kontinuerlig = 'kontinuerlig',
  Midtveisvurdering = 'midtveisvurdering',
  Sluttvurdering = 'sluttvurdering',
}

enum VurderingsinnstillingerActionType {
  SET_ROLLER,
  SET_VURDERINGSTYPER,
  SET_INITIAL_STATE,
}

type VurderingsinnstillingerAction =
  | GenericActionWithPayload<
      VurderingsinnstillingerActionType.SET_ROLLER,
      VurderingsrollerDto
    >
  | GenericActionWithPayload<
      VurderingsinnstillingerActionType.SET_VURDERINGSTYPER,
      VurderingstyperDto
    >
  | GenericActionWithPayload<
      VurderingsinnstillingerActionType.SET_INITIAL_STATE,
      VurderingsinnstillingerDto
    >;

type VurderingsinnstillingerState = {
  initialSettings: VurderingsinnstillingerDto;
  currentSettings: VurderingsinnstillingerDto;
};

const vurderingsinnstillingerReducer: Reducer<
  VurderingsinnstillingerState,
  VurderingsinnstillingerAction
> = (state, action) => {
  switch (action.type) {
    case VurderingsinnstillingerActionType.SET_ROLLER:
      return {
        ...state,
        currentSettings: {
          ...state.currentSettings,
          roller: action.payload,
        },
      };
    case VurderingsinnstillingerActionType.SET_VURDERINGSTYPER:
      return {
        ...state,
        currentSettings: {
          ...state.currentSettings,
          vurderingstyper: action.payload,
        },
      };
    case VurderingsinnstillingerActionType.SET_INITIAL_STATE:
      return {
        ...state,
        initialSettings: action.payload,
      };
  }
  return state;
};

type VurderingsinnstillingerProps = {
  vurderingsinnstillinger: VurderingsinnstillingerDto;
};

const getVurderingsinnstillingerState = (
  vurderingsinnstillinger: VurderingsinnstillingerDto,
): VurderingsinnstillingerState => ({
  initialSettings: vurderingsinnstillinger,
  currentSettings: { ...vurderingsinnstillinger, usesVurdering: true },
});

const vurderingstypeCheckboxDisabled = (
  currentSettings: VurderingsinnstillingerDto,
  vurderingstype: Vurderingstyper,
) => {
  switch (vurderingstype) {
    case Vurderingstyper.Kontinuerlig:
      return true;
    case Vurderingstyper.Midtveisvurdering:
      return !currentSettings.vurderingstyper[Vurderingstyper.Sluttvurdering];
    default:
      return false;
  }
};

const rollerAreEqual = (
  {
    underviser: initUnderviser,
    student: initStudent,
    veileder: initVeileder,
  }: VurderingsrollerDto,
  {
    underviser: currentUnderviser,
    student: currentStudent,
    veileder: currentVeileder,
  }: VurderingsrollerDto,
): boolean =>
  initUnderviser === currentUnderviser &&
  initStudent === currentStudent &&
  initVeileder === currentVeileder;

const vurderingstyperAreEqual = (
  {
    kontinuerlig: initKontinuerlig,
    midtveisvurdering: initMidtveisvurdering,
    sluttvurdering: initSluttvurdering,
  }: VurderingstyperDto,
  {
    kontinuerlig: currentKontinuerlig,
    midtveisvurdering: currentMidtveisvurdering,
    sluttvurdering: currentSluttvurdering,
  }: VurderingstyperDto,
): boolean =>
  initKontinuerlig === currentKontinuerlig &&
  initMidtveisvurdering === currentMidtveisvurdering &&
  initSluttvurdering === currentSluttvurdering;

const instillingerAreEqual = (
  {
    roller: initRoller,
    vurderingstyper: initVurderingstyper,
    usesVurdering: initUsesVurdering,
  }: VurderingsinnstillingerDto,
  {
    roller: currentRoller,
    vurderingstyper: currentVurderingstyper,
    usesVurdering: currentUsesVurdering,
  }: VurderingsinnstillingerDto,
): boolean =>
  rollerAreEqual(initRoller, currentRoller) &&
  vurderingstyperAreEqual(initVurderingstyper, currentVurderingstyper) &&
  initUsesVurdering === currentUsesVurdering;

const Vurderingsinnstillinger: React.FC<
  React.PropsWithChildren<VurderingsinnstillingerProps>
> = ({ vurderingsinnstillinger }) => {
  const [
    { initialSettings, currentSettings },
    dispatchVurderingsinnstillinger,
  ] = useReducer(
    vurderingsinnstillingerReducer,
    getVurderingsinnstillingerState(vurderingsinnstillinger),
  );

  const { praksisaktivitetVurderingApi } = useContext(StudiestedApiContext);

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      vurderingsinnstillingerSaveSuccess: formatMessage({
        id: translations.vurderingsinnstillingerSaveSuccess.id,
      }),
      vurderingstypeLabel: formatMessage({
        id: translations.vurderingstypeLabel.id,
      }),
      kontinuerlig: formatMessage({
        id: translations.kontinuerlig.id,
      }),
      midtveisvurdering: formatMessage({
        id: translations.midtveisvurdering.id,
      }),
      sluttvurdering: formatMessage({
        id: translations.sluttvurdering.id,
      }),
      vurderingsrollerLabel: formatMessage({
        id: translations.vurderingsrollerLabel.id,
      }),
      underviser: formatMessage({
        id: translations.underviser.id,
      }),
      veileder: formatMessage({
        id: translations.veileder.id,
      }),
      student: formatMessage({
        id: translations.student.id,
      }),
      vurderingsinnstillingerSaveMidtveisError: formatMessage({
        id: translations.vurderingsinnstillingerSaveMidtveisError.id,
      }),
      buttonSaveLabel: formatMessage({
        id: translations.buttonSaveLabel.id,
      }),
    }),
    [formatMessage],
  );

  const invalidMidtveisvurderingState = useMemo(
    () =>
      currentSettings.vurderingstyper[Vurderingstyper.Midtveisvurdering] &&
      !currentSettings.vurderingstyper[Vurderingstyper.Sluttvurdering],
    [currentSettings.vurderingstyper],
  );
  const {
    praksisaktivitet: { id: aktivitetId },
  } = useActivityContextUHA();
  const { addSuccessNotification, addErrorNotification } = useContext(
    NotificationFunctionContext,
  );

  const lagreBtnDisabled = useMemo(
    () => instillingerAreEqual(initialSettings, currentSettings),
    [initialSettings, currentSettings],
  );

  const onSave = () => {
    if (!invalidMidtveisvurderingState) {
      praksisaktivitetVurderingApi
        .praksisaktivitetAktivitetIdVurderingInnstillingerPut({
          pathParams: {
            aktivitetId,
          },
          vurderingsinnstillingerDto: currentSettings,
        })
        .then((res) => {
          addSuccessNotification(labels.vurderingsinnstillingerSaveSuccess);
          dispatchVurderingsinnstillinger({
            type: VurderingsinnstillingerActionType.SET_INITIAL_STATE,
            payload: res,
          });
        })
        .catch((err: HttpError) => addErrorNotification(err.body));
    }
  };

  const handleCheckedRolle = useCallback(
    (checked: boolean, value: Roller) => {
      dispatchVurderingsinnstillinger({
        type: VurderingsinnstillingerActionType.SET_ROLLER,
        payload: {
          ...currentSettings.roller,
          [value]: checked,
        },
      });
    },
    [currentSettings.roller],
  );

  const rollerCheckboxes = useMemo(
    () =>
      Object.values(Roller).map((rolle) => (
        <div key={rolle} className={css.checkboxElement}>
          <Checkbox
            id={`select-rolle-${rolle}`}
            checked={currentSettings.roller[rolle]}
            setChecked={(checked) => handleCheckedRolle(checked, rolle)}
            key={rolle}
            label={labels[rolle]}
          />
        </div>
      )),
    [currentSettings, handleCheckedRolle, labels],
  );

  const handleCheckedVurderingstype = useCallback(
    (checked: boolean, value: Vurderingstyper) => {
      if (value === Vurderingstyper.Sluttvurdering && !checked) {
        dispatchVurderingsinnstillinger({
          type: VurderingsinnstillingerActionType.SET_VURDERINGSTYPER,
          payload: {
            ...currentSettings.vurderingstyper,
            [value]: checked,
            midtveisvurdering: false,
          },
        });
      } else {
        dispatchVurderingsinnstillinger({
          type: VurderingsinnstillingerActionType.SET_VURDERINGSTYPER,
          payload: {
            ...currentSettings.vurderingstyper,
            [value]: checked,
          },
        });
      }
    },
    [currentSettings.vurderingstyper],
  );

  const vurderingstyperCheckboxes = useMemo(
    () =>
      Object.values(Vurderingstyper).map((vurderingstype) => (
        <div key={vurderingstype} className={css.checkboxElement}>
          <Checkbox
            id={`select-vurderingstype-${vurderingstype}`}
            checked={currentSettings.vurderingstyper[vurderingstype]}
            setChecked={(checked) =>
              handleCheckedVurderingstype(checked, vurderingstype)
            }
            key={vurderingstype}
            label={labels[vurderingstype]}
            disabled={vurderingstypeCheckboxDisabled(
              currentSettings,
              vurderingstype,
            )}
          />
        </div>
      )),
    [currentSettings, handleCheckedVurderingstype, labels],
  );

  return (
    <div className={css.section}>
      <VurderingsinstillingerRow
        label={labels.vurderingsrollerLabel}
        checkboxes={rollerCheckboxes}
      />
      <VurderingsinstillingerRow
        label={labels.vurderingstypeLabel}
        checkboxes={vurderingstyperCheckboxes}
      />
      <div className={css.button}>
        <Button.Secondary
          onClick={onSave}
          disabled={lagreBtnDisabled}
          data-cy="save-button"
        >
          {labels.buttonSaveLabel}
        </Button.Secondary>
      </div>
    </div>
  );
};

type VurderingsinstillingerRowType = {
  label: string;
  checkboxes: JSX.Element[];
};

const VurderingsinstillingerRow: React.FC<
  React.PropsWithChildren<VurderingsinstillingerRowType>
> = ({ label, checkboxes }) => {
  return (
    <div className={css.row}>
      <span className={css.title}>{label}</span>
      <div className={css.checkboxElements}>{checkboxes}</div>
    </div>
  );
};

export default Vurderingsinnstillinger;
